import React, { FC } from 'react';
import classNames from 'classnames';
import { ShopifyProduct, ShopifyVariant } from '@/lib/shopify';
import {
  TypeAsset,
  TypeOptionsValues,
  TypeProduct as InternalProduct,
} from '@/lib/api/productCatalog/types';
import styles from '@/components/ui/Select/Select.module.scss';
import { removeUnderscoresFromString } from '@/utils/strings';
import { ORGANIC_COTTON_BABY_DOLL_COLOR_MAP } from '@/utils/products';
import Image from 'next/image';
import useProductContext from '@/context/ProductContext';

const VariantDropdown = ({
  optionName,
  handleChange,
  variantTitle,
  optionValues,
}: {
  optionName: string;
  handleChange: (ev: any) => void;
  variantTitle: string | string[];
  optionValues: TypeOptionsValues[];
}) => {
  return (
    <select
      name={optionName}
      onChange={handleChange}
      value={variantTitle}
      className={`select w-full rounded-sm border border-grey-midtone text-lg ${styles.select}`}
    >
      {optionValues.map(({ localized_label, value }) => (
        <option key={value} value={localized_label}>
          {localized_label}
        </option>
      ))}
    </select>
  );
};

const VariantBabyDollSelect = ({
  optionValues,
  variantTitle,
  handleChange,
}: {
  optionValues: TypeOptionsValues[];
  variantTitle: string[];
  handleChange: (ev: any) => void;
}) => {
  return (
    <ul className="flex">
      {optionValues?.map(({ localized_label, value }) => {
        const isOrganicCottonBabyDollSkinToneSelected =
          variantTitle.includes(localized_label);
        return (
          <li
            key={value}
            className={classNames(
              'mr-2 flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full border',
              isOrganicCottonBabyDollSkinToneSelected
                ? 'border-charcoal'
                : 'border-transparent',
            )}
            onClick={() => handleChange(localized_label)}
          >
            <div
              className="h-[33px] w-[33px] rounded-full"
              style={{
                backgroundColor:
                  ORGANIC_COTTON_BABY_DOLL_COLOR_MAP[localized_label],
              }}
            />
          </li>
        );
      })}
    </ul>
  );
};

const VariantTileSelect = ({
  optionValues,
  optionAssets,
  handleChange,
  variantTitle,
}: {
  optionValues: TypeOptionsValues[];
  optionAssets: TypeAsset[];
  handleChange: (label: string) => void;
  variantTitle: string | string[];
}) => {
  return (
    <ul className="flex flex-wrap">
      {optionValues?.map(({ localized_label, value }, i) => {
        const isSelected = variantTitle.includes(localized_label);
        return (
          <li key={value} className="mr-2 w-[60px] text-center lg:w-[94px]">
            <Image
              src={optionAssets[i].uri} // TODO: assuming that the optionAssets are in the same order as the optionValues
              height={94}
              width={94}
              alt={localized_label}
              className={classNames(
                'cursor-pointer rounded border border-[3px] border-solid',
                isSelected ? 'border-indigo' : 'border-transparent',
              )}
              onClick={() => handleChange(localized_label)}
            />
            <span className="hidden text-sm capitalize lg:block">
              {localized_label}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

interface VariantSelectProps {
  selectedVariant: ShopifyVariant;
  setSelectedVariant: (option: ShopifyVariant) => void;
  selectLabel: string;
}

const VariantSelect: FC<VariantSelectProps> = ({
  selectedVariant,
  setSelectedVariant,
  selectLabel,
}) => {
  const { internalProduct, shopifyProduct } = useProductContext();
  const { anchor, customizable_options, assets } =
    internalProduct as InternalProduct;
  const { variants } = shopifyProduct as ShopifyProduct;

  const variantTitle = selectedVariant.title.includes('/')
    ? selectedVariant.title.split(' / ')
    : selectedVariant.title;

  const handleOptionsChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const optionLabel = event.currentTarget.value;
    const variant = variants.find((v) => v.title === optionLabel);
    if (variant) {
      setSelectedVariant(variant);
    }
  };

  const handleMultipleOptionsChange = ({
    event,
    label = '',
    customizableOptionsIndex,
    variantTitle,
  }: {
    event?: React.ChangeEvent<HTMLSelectElement>;
    label?: string;
    customizableOptionsIndex: number;
    variantTitle: string[];
  }) => {
    variantTitle[customizableOptionsIndex] = event
      ? event?.currentTarget.value
      : label;
    const newVariantTitle = variantTitle.join(' / ');
    const variant = variants.find((v) => v.title === newVariantTitle);
    if (variant) {
      setSelectedVariant(variant);
    }
  };

  const handleSelectOptionsChange = (label: string) => {
    const variant = variants.find((v) => v.title === label);
    if (variant) {
      setSelectedVariant(variant);
    }
  };

  const getVariantTitle = (variantTitle: string | string[]) => {
    return Array.isArray(variantTitle) ? variantTitle[0] : variantTitle;
  };

  return (
    <div className="mt-5 border-t border-grey-midtone pt-5">
      {customizable_options?.map(({ option_name, option_values }, i) => {
        const VARIANT_SELECT_MAP: {
          [option_name: string]: React.JSX.Element;
        } = {
          doll_skin_tone: (
            <VariantBabyDollSelect
              optionValues={option_values}
              variantTitle={variantTitle as string[]}
              handleChange={(param) =>
                customizable_options.length > 1
                  ? handleMultipleOptionsChange({
                      label: param,
                      customizableOptionsIndex: i,
                      variantTitle: variantTitle as string[],
                    })
                  : handleSelectOptionsChange(param)
              }
            />
          ),
          type: (
            <VariantTileSelect
              optionValues={option_values}
              optionAssets={assets as TypeAsset[]}
              handleChange={handleSelectOptionsChange}
              variantTitle={
                customizable_options.length > 1 ? variantTitle[i] : variantTitle
              }
            />
          ),
        };
        return (
          <div key={`${anchor}-${option_name}`} className="mb-3">
            <p className="mb-2">
              {selectLabel}{' '}
              {removeUnderscoresFromString({
                str: option_name,
                isCapitalized: true,
              })}
              :{' '}
              {VARIANT_SELECT_MAP[option_name]
                ? getVariantTitle(variantTitle)
                : null}
            </p>
            {VARIANT_SELECT_MAP[option_name] !== undefined ? (
              VARIANT_SELECT_MAP[option_name]
            ) : (
              <VariantDropdown
                optionName={option_name}
                optionValues={option_values}
                variantTitle={
                  customizable_options.length > 1
                    ? variantTitle[i]
                    : variantTitle
                }
                handleChange={(ev) =>
                  customizable_options.length > 1
                    ? handleMultipleOptionsChange({
                        event: ev,
                        customizableOptionsIndex: i,
                        variantTitle: variantTitle as string[],
                      })
                    : handleOptionsChange(ev)
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default VariantSelect;
